import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PhoneCall, Mic, Square, Loader2, Sparkles, ShieldCheck, Keyboard,
  Plus, Trash2, CalendarClock, Building2, FileText, Send, Link2, X,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { VOICE_FEATURES_ENABLED } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const pad = (n) => String(n).padStart(2, "0");
const nowParts = () => { const d = new Date(); return { call_date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, call_time: `${pad(d.getHours())}:${pad(d.getMinutes())}` }; };
const EMPTY = {
  call_type: "phone", company: "", contact_name: "", contact_phone: "",
  transcript: "", summary: "", minutes: "", action_points: [],
  linked_type: null, linked_id: null, linked_name: "",
};

const TAG_META = {
  universe: { label: "Universe", plural: "Universe companies", icon: Building2, cls: "bg-slate-100 text-slate-700" },
  lead: { label: "Lead", plural: "Leads", icon: FileText, cls: "bg-sky-50 text-sky-700" },
  opportunity: { label: "Opportunity", plural: "Opportunities", icon: Send, cls: "bg-indigo-50 text-indigo-700" },
};

export function CompanyTagPicker({ value, linked, onChange, onPick, onUnlink }) {
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (linked?.linked_id || (value || "").trim().length < 2) { setResults(null); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/calls/tag-search", { params: { q: value } });
        setResults(data);
        setOpen((data.universe?.length || 0) + (data.leads?.length || 0) + (data.opportunities?.length || 0) > 0);
      } catch { setResults(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [value, linked?.linked_id]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const groups = results ? [
    ["universe", results.universe || []],
    ["lead", results.leads || []],
    ["opportunity", results.opportunities || []],
  ].filter(([, arr]) => arr.length) : [];

  return (
    <div className="relative" ref={boxRef}>
      <Input value={value} onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (groups.length) setOpen(true); }}
        className="h-9" placeholder="Search Universe, Leads, Opportunities…" data-testid="call-company" />
      {linked?.linked_id && (
        <span className={`mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded ${TAG_META[linked.linked_type]?.cls || "bg-slate-100 text-slate-700"}`} data-testid="call-tag-chip">
          <Link2 className="h-3 w-3" /> {TAG_META[linked.linked_type]?.label}: {linked.linked_name || value}
          <button type="button" onClick={onUnlink} className="hover:opacity-70" data-testid="call-tag-unlink"><X className="h-3 w-3" /></button>
        </span>
      )}
      {open && !linked?.linked_id && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="call-tag-suggestions">
          {groups.map(([type, arr]) => {
            const Meta = TAG_META[type];
            return (
              <div key={type}>
                <p className="px-2.5 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{Meta.plural}</p>
                {arr.map((r) => (
                  <button key={`${type}-${r.id}`} type="button"
                    onClick={() => { setOpen(false); onPick(type, r); }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-sm hover:bg-slate-50"
                    data-testid={`call-tag-option-${type}-${r.id}`}>
                    <Meta.icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span className="truncate text-slate-800">{r.label}</span>
                    {r.sub && <span className="ml-auto text-[11px] text-slate-400 truncate max-w-[45%]">{r.sub}</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function pickMime() {
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const t of types) if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
  return "";
}

export function RecordCallButton({ onSaved, compact = false }) {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const mr = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const timer = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAP = (i, k, v) => setForm((f) => {
    const aps = [...f.action_points];
    aps[i] = { ...aps[i], [k]: v };
    return { ...f, action_points: aps };
  });
  const addAP = () => setForm((f) => ({ ...f, action_points: [...f.action_points, { text: "", due_date: "", due_time: "", done: false }] }));
  const removeAP = (i) => setForm((f) => ({ ...f, action_points: f.action_points.filter((_, x) => x !== i) }));

  const openManual = () => {
    setForm({ ...EMPTY, ...nowParts() });
    setPrivacyOpen(false);
    setReviewOpen(true);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Recording isn't supported on this browser. Use Type manually.");
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Microphone permission denied.");
      return;
    }
    const mime = pickMime();
    chunks.current = [];
    mr.current = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    mr.current.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
    mr.current.onstop = () => upload(mime);
    mr.current.start();
    setPrivacyOpen(false);
    setRecording(true);
    setSeconds(0);
    timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const stopRecording = () => {
    clearInterval(timer.current);
    setRecording(false);
    if (mr.current && mr.current.state !== "inactive") mr.current.stop();
    if (stream.current) stream.current.getTracks().forEach((t) => t.stop());
  };

  const upload = async (mime) => {
    setProcessing(true);
    try {
      const ext = (mime || "audio/webm").includes("mp4") ? "mp4" : (mime || "").includes("ogg") ? "ogg" : "webm";
      const blob = new Blob(chunks.current, { type: mime || "audio/webm" });
      if (blob.size < 1200) { toast.error("Recording too short. Try again."); return; }
      const fd = new FormData();
      fd.append("audio", blob, `call.${ext}`);
      fd.append("call_type", form.call_type || "phone");
      const { data } = await api.post("/calls/process", fd);
      if (!data.transcript) { toast.error("Couldn't hear anything. Try recording again."); return; }
      setForm((f) => ({
        ...f,
        ...nowParts(),
        transcript: data.transcript,
        summary: data.summary || "",
        minutes: data.minutes || "",
        action_points: (data.action_points || []).map((a) => ({
          text: a.text || "", due_date: a.due_date || "", due_time: a.due_time || "", done: false,
        })),
      }));
      setReviewOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setProcessing(false);
    }
  };

  const save = async () => {
    if (!form.summary.trim() && !form.minutes.trim()) {
      toast.error("Add a summary or minutes before saving.");
      return;
    }
    setSaving(true);
    try {
      const aps = form.action_points
        .map((a) => ({ ...a, text: (a.text || "").trim() }))
        .filter((a) => a.text);
      const { call_date, call_time, ...rest } = form;
      const when = call_date ? new Date(`${call_date}T${call_time || "12:00"}:00`) : null;
      if (when && when.getTime() > Date.now() + 60000) { toast.error("Call date/time cannot be in the future."); setSaving(false); return; }
      await api.post("/calls", { ...rest, action_points: aps, ...(when ? { call_at: when.toISOString() } : {}) });
      const reminders = aps.filter((a) => a.due_date && !a.done).length;
      toast.success(reminders > 0
        ? `Call logged · ${reminders} reminder${reminders > 1 ? "s" : ""} scheduled`
        : "Call logged");
      setReviewOpen(false);
      setForm(EMPTY);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {compact ? (
        <button
          onClick={() => (VOICE_FEATURES_ENABLED ? setPrivacyOpen(true) : openManual())}
          disabled={processing}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium bg-brand text-white hover:bg-brand/90 transition-colors disabled:opacity-60"
          data-testid="record-call-btn"
        >
          {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
          <span className="hidden sm:inline">{processing ? "Processing…" : "Log Call"}</span>
        </button>
      ) : (
        <Button onClick={() => (VOICE_FEATURES_ENABLED ? setPrivacyOpen(true) : openManual())} disabled={processing}
          className="gap-2 bg-brand hover:bg-brand/90 text-white" data-testid="record-call-btn">
          {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />}
          {processing ? "Processing…" : "Log a Call"}
        </Button>
      )}

      {/* Privacy + consent */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-md" data-testid="call-privacy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-brand" /> Record your call
            </DialogTitle>
            <DialogDescription>AI turns the recording into clean minutes & dated action points.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="call-privacy-guidelines">
            <li className="flex gap-2"><span className="text-brand">•</span> Record only when all parties consent, as per local laws.</li>
            <li className="flex gap-2"><span className="text-brand">•</span> Only business-relevant content is captured — small talk and personal chatter are ignored.</li>
            <li className="flex gap-2"><span className="text-brand">•</span> The recording is transcribed (Hindi/English supported), summarised, then discarded — only the text minutes are saved to your CRM.</li>
            <li className="flex gap-2"><span className="text-brand">•</span> You can review and edit everything before saving.</li>
          </ul>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={openManual} className="gap-1.5 sm:mr-auto" data-testid="call-type-manual-btn">
              <Keyboard className="h-4 w-4" /> Type manually
            </Button>
            <Button onClick={startRecording} className="gap-1.5 bg-brand hover:bg-brand/90 text-white" data-testid="call-start-recording-btn">
              <Mic className="h-4 w-4" /> I have consent — Start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Active recording */}
      <Dialog open={recording} onOpenChange={(o) => { if (!o) stopRecording(); }}>
        <DialogContent className="max-w-xs text-center" data-testid="call-recording-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center justify-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-red-600 animate-pulse" /> Recording…
            </DialogTitle>
            <DialogDescription className="sr-only">Recording in progress</DialogDescription>
          </DialogHeader>
          <p className="text-3xl font-bold font-heading tabular-nums text-slate-900" data-testid="call-record-timer">
            {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
          </p>
          <Button onClick={stopRecording} className="w-full gap-1.5 bg-red-600 hover:bg-red-700 text-white" data-testid="call-stop-recording-btn">
            <Square className="h-4 w-4" /> Stop & Transcribe
          </Button>
        </DialogContent>
      </Dialog>

      {/* Review + edit + save */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="call-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand" /> Review Call Minutes</DialogTitle>
            <DialogDescription>Edit before saving. Dated action points become Web Push reminders.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Call date</Label>
                <Input type="date" value={form.call_date || ""} max={nowParts().call_date} onChange={(e) => set("call_date", e.target.value)} className="h-9" data-testid="call-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Call time</Label>
                <Input type="time" value={form.call_time || ""} onChange={(e) => set("call_time", e.target.value)} className="h-9" data-testid="call-time" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Call type</Label>
                <Select value={form.call_type} onValueChange={(v) => set("call_type", v)}>
                  <SelectTrigger className="h-9" data-testid="call-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone call</SelectItem>
                    <SelectItem value="in_person">In-person</SelectItem>
                    <SelectItem value="video">Video call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Company / Client</Label>
                <CompanyTagPicker value={form.company}
                  linked={{ linked_type: form.linked_type, linked_id: form.linked_id, linked_name: form.linked_name }}
                  onChange={(v) => setForm((f) => ({ ...f, company: v, linked_type: null, linked_id: null, linked_name: "" }))}
                  onPick={(type, r) => setForm((f) => ({ ...f, company: r.company || r.label, linked_type: type, linked_id: r.id, linked_name: r.label }))}
                  onUnlink={() => setForm((f) => ({ ...f, linked_type: null, linked_id: null, linked_name: "" }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-700">Contact</Label>
                <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="h-9" placeholder="Person spoken to" data-testid="call-contact" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Summary</Label>
              <Textarea value={form.summary} onChange={(e) => set("summary", e.target.value)} rows={2} placeholder="2-3 sentence recap of the call" data-testid="call-summary" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Minutes of Meeting</Label>
              <Textarea value={form.minutes} onChange={(e) => set("minutes", e.target.value)} rows={5} placeholder="- Discussed…&#10;- Decided…&#10;- Next steps…" className="font-mono text-xs" data-testid="call-minutes" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-slate-700 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Action points</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAP} className="h-7 gap-1 text-xs" data-testid="call-add-action-point">
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
              {form.action_points.length === 0 && (
                <p className="text-xs text-slate-400 italic">No action points. Add one to get a reminder.</p>
              )}
              {form.action_points.map((ap, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2" data-testid={`call-action-point-${i}`}>
                  <Checkbox checked={ap.done} onCheckedChange={(v) => setAP(i, "done", !!v)} className="flex-shrink-0" data-testid={`call-ap-done-${i}`} />
                  <Input value={ap.text} onChange={(e) => setAP(i, "text", e.target.value)} className="h-8 flex-1 min-w-[160px]" placeholder="Task to follow up" data-testid={`call-ap-text-${i}`} />
                  <Input type="date" value={ap.due_date || ""} onChange={(e) => setAP(i, "due_date", e.target.value)} className="h-8 w-[140px]" data-testid={`call-ap-date-${i}`} />
                  <Input type="time" value={ap.due_time || ""} onChange={(e) => setAP(i, "due_time", e.target.value)} className="h-8 w-[110px]" data-testid={`call-ap-time-${i}`} />
                  <button type="button" onClick={() => removeAP(i)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`call-ap-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {form.transcript && (
              <details className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
                <summary className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold cursor-pointer">Raw transcript</summary>
                <p className="text-xs text-slate-500 italic mt-2">"{form.transcript}"</p>
              </details>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} data-testid="call-cancel">Discard</Button>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-2" data-testid="call-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Call Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
